import { useIntl } from 'react-intl';
import { formatMoney, formatDate } from '@repo/design-system-kit';
import CURRENCIES from 'receivable-invoices-js/constants/currencies';
import {
  FREQUENCY_UNITS,
  PAYMENT_FREQUENCIES,
} from 'receivable-invoices-js/constants/sepa-direct-debits';
import type { Amount } from 'receivable-invoices-js/react/types/amount';

interface UseHeaderDataProps {
  amount: Amount;
  dueDate: string;
  isRecurring: boolean;
  isOverdue?: boolean;
  recurringFrequency?: string;
  recurringInterval?: number;
  recurringUnit?: string;
}

export function useHeaderData({
  amount,
  dueDate: dueDateValue,
  isRecurring,
  isOverdue,
  recurringFrequency,
  recurringInterval,
  recurringUnit,
}: UseHeaderDataProps) {
  const intl = useIntl();

  const title = isRecurring
    ? intl.formatMessage({ id: 'sdd-collections.payment-link-page.title.recurring' })
    : intl.formatMessage({ id: 'sdd-collections.payment-link-page.title.one-off' });

  const frequency = (() => {
    const unitLabels = {
      [FREQUENCY_UNITS.MONTHS]: intl.formatMessage({ id: 'sdd-collections.recurrence.months' }),
      [FREQUENCY_UNITS.YEARS]: intl.formatMessage({ id: 'sdd-collections.recurrence.years' }),
      [FREQUENCY_UNITS.WEEKS]: intl.formatMessage({ id: 'sdd-collections.recurrence.weeks' }),
    };

    const frequencyLabels = {
      [PAYMENT_FREQUENCIES.MONTHLY]: intl.formatMessage({
        id: 'sdd-collections.recurrence.slash.every-month',
      }),
      [PAYMENT_FREQUENCIES.QUARTERLY]: intl.formatMessage({
        id: 'sdd-collections.recurrence.slash.every-quarter',
      }),
      [PAYMENT_FREQUENCIES.YEARLY]: intl.formatMessage({
        id: 'sdd-collections.recurrence.slash.every-year',
      }),
      [PAYMENT_FREQUENCIES.WEEKLY]: intl.formatMessage({
        id: 'sdd-collections.recurrence.slash.every-week',
      }),
      [PAYMENT_FREQUENCIES.CUSTOM]: intl.formatMessage(
        { id: 'sdd-collections.recurrence.slash.every-custom' },
        {
          number: recurringInterval,
          recurrence: recurringUnit ? unitLabels[recurringUnit] : '',
        }
      ),
    };

    return recurringFrequency ? frequencyLabels[recurringFrequency] : '';
  })();

  const currency = amount.currency || CURRENCIES.default;

  const dueDate =
    formatDate({
      date: dueDateValue,
      token: 'compact',
      locale: intl.locale,
    }) || dueDateValue;

  const disclaimer = (() => {
    if (isOverdue) {
      if (isRecurring) {
        return intl.formatMessage({
          id: 'sdd-collections.payment-link-page.disclaimers.due-date.recurring',
        });
      }

      return intl.formatMessage({
        id: 'sdd-collections.payment-link-page.disclaimers.due-date.on-off',
      });
    }
    return null;
  })();

  const showDisclaimer = Boolean(disclaimer);

  const formattedAmount = formatMoney({
    amount: amount.value,
    currency,
    token: 'money-symbol',
    locale: intl.locale,
  });

  return {
    title,
    frequency,
    dueDate,
    disclaimer,
    showDisclaimer,
    formattedAmount,
  };
}
